import { useEffect, useRef, useState } from "react";
import { ArticleSpecs } from "../../../models/articleModel";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { db } from "../../../data/db/db";
import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon, isPlatform, IonModal, useIonAlert, AlertInput } from "@ionic/react";
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { scanOutline, arrowDownOutline, createOutline } from "ionicons/icons";
import { AsyncAutocomplete, AsyncMaterialsAutocomplete, MLKitScanner } from "../../../components";
import { CalculatorMillerForm } from "./FormTypes";
import { getMaterialLabel } from "./formHelpers";
import { BarcodeScanner } from "@capacitor-mlkit/barcode-scanning";
import { defaultScannerOptions, installGoogleBarcodeScannerModule, requestPermissions, urlToIdent } from "../../../components/mlKitScanner/qrCodeHelper";
import { useTranslation } from "react-i18next";

interface MillerFormProps {
    submitForm: (data: any) => void,
    editBtnCallback: () => void
    isFormShowing: boolean,
    formData: CalculatorMillerForm | undefined,
}


const MillerForm: React.FC<MillerFormProps> = ({ submitForm, isFormShowing, editBtnCallback, formData }: MillerFormProps) => {
    const { control, watch, handleSubmit, setValue, getValues, resetField, formState: { errors } } = useForm<CalculatorMillerForm>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            article: null,
            ident: "",
            diameter: "",
            pos: "",
            material: null,
        }
    });
    const [variantOptions, setVariantOptions] = useState<ArticleSpecs[]>([])
    const [diameters, setDiameters] = useState<string[]>([]);
    const [data, setData] = useState<CalculatorMillerForm | undefined>()
    const [isScannerSupported, setIsSCannerSupported] = useState<boolean>(false);
    const [scanActive, setScanActive] = useState<boolean>(false);
    const modal = useRef<HTMLIonModalElement>(null!);
    const [presentIdentAlert] = useIonAlert();
    const { t } = useTranslation();

    const onArticleChange = async (val: any) => {
        let articleIdent = val;

        if (articleIdent) {
            let [ident, pos] = articleIdent.split("-")

            if (getValues("pos") !== "") {
                resetField("pos")
            }
            if (getValues("diameter") !== "") {
                resetField("diameter")
            }
            if (getValues("material") !== null) {
                resetField("material")
            }
            setValue("article", val)
            setValue("ident", ident)
            const articlesFetched = await db.articlesSpec
                .where('ident')
                .anyOf(ident)
                .toArray()

            setVariantOptions(articlesFetched)


            let tempDiameters: string[] = [];
            articlesFetched?.forEach(article => {
                if (!tempDiameters.includes(article.d1.toString())) {
                    tempDiameters.push(article.d1.toString())
                }
            })
            setDiameters(tempDiameters);

            if (pos) {
                let match = articlesFetched.find(x => x.pos === pos)
                if (match) { setValue("diameter", match.d1.toString()) }

                setValue("pos", pos)
            }
        }
    }

    useEffect(() => {
        let ignore = false;

        BarcodeScanner.isSupported().then((res) => {
            if (!ignore) {
                setIsSCannerSupported(res.supported)
            }
        }).catch(() => { })

        return () => { ignore = true };

    }, [])

    useEffect(() => {
        let ignore = false
        if (formData !== undefined) {
            if (formData.ident && formData.pos) {
                if (!ignore) {
                    onArticleChange(formData.ident.replaceAll("-", " ") + "-" + formData.pos)
                    if (!isFormShowing) {
                        setData(formData)
                    }
                }
            }
            if (formData.material) {
                setValue("material", formData.material)
            }
        }

        return (() => { ignore = true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData])

    const selectedArticle = watch("article");
    const selectedIdent = watch("ident");
    const selectedDiameter = watch("diameter");
    const selectedPos = watch("pos");


    useEffect(() => {
        let tempDiameters: string[] = [];
        variantOptions?.forEach(article => {
            if (!tempDiameters.includes(article.d1.toString())) {
                tempDiameters.push(article.d1.toString())
            }
        })
        setDiameters(tempDiameters);
    }, [variantOptions])



    const onDiameterChange = (e: SelectChangeEvent) => {
        if (getValues("pos") !== "") {
            resetField("pos")
        }
        if (getValues("material") !== null) {
            resetField("material")
        }

        setValue("diameter", e.target.value)
    }

    const onPosChange = (e: SelectChangeEvent) => {
        if (getValues("material") !== null) {
            resetField("material")
        }
        setValue("pos", e.target.value)
    }


    const onSubmit: SubmitHandler<CalculatorMillerForm> = data => {
        setData(data)
        submitForm(data)
    }


    //QR Code
    const startScan = async () => {
        const granted = await requestPermissions();
        if (!granted) {
            //TODO: display error info
            return
        }
        if (isPlatform("android")) {
            const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
            if (!available) {
                await installGoogleBarcodeScannerModule()
            }
        }
        // Add the `barcodeScanned` listener
        await BarcodeScanner.addListener(
            'barcodesScanned',
            async (result) => {
                stopScan()
                handleQRCodeResult(result.barcodes[0].displayValue);// TODO: nach cap 7 update üebrprüfen was alles in array ist
            },
        )
        // Start the barcode scanner
        setScanActive(true)
        document.querySelector('body')?.classList.add('barcode-scanner-active');
        await BarcodeScanner.startScan(defaultScannerOptions);
    }

    const stopScan = async () => {
        document.querySelector('body')?.classList.remove('barcode-scanner-active');
        setScanActive(false)
        await BarcodeScanner.removeAllListeners();

        await BarcodeScanner.stopScan();
    };

    const handleQRCodeResult = (url_string: string) => {
        let { params } = urlToIdent(url_string)
        if (params.length > 1) {
            let inputs: AlertInput[] = []
            params.forEach(param => {
                param = formatIdent(param)
                inputs.push({ label: param, type: "radio", value: param })
            })
            presentIdentAlert({
                header: t('calculatorForm.MillerForm.Artikel auswählen'),
                inputs: inputs,
                buttons: [{
                    text: t("calculatorForm.MillerForm.Abbrechen"),
                    role: "cancel"
                }, {
                    text: t("calculatorForm.MillerForm.OK"),
                    handler: (val) => {
                        if (val !== undefined) {
                            onArticleChange(val)
                        }
                    }
                }],
            })
        } else {
            onArticleChange(formatIdent(params[0]))
        }

    }

    const formatIdent = (ident: string) => {
        let space = [3, 5];
        let M = ident.length, N = space.length, l = 0, r = 0;
        let res = new Array(M + N).fill(' ');

        // Iterate over M+N length
        for (let i = 0; i < M + N; i++) {

            if (l < N && i === space[l] + l)
                l++;
            else
                res[i] = ident[r++];
        }

        // Return the required string
        return res.join('');
    }


    if (isFormShowing) {
        return (
            <>
                <IonCard style={{ marginTop: 20 }}>
                    <IonCardHeader>
                        <IonCardTitle>{t("calculatorForm.MillerForm.Fräser")} & {t("calculatorForm.MillerForm.Material")}</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <form>
                            <div className="stepTitle">{t("calculatorForm.MillerForm.Fräser")}</div>

                            {/* Artikelnummer */}
                            <div style={{ display: "flex", gap: 16 }}>
                                <div style={{ width: "100%" }}>
                                    <Controller
                                        render={({ field: { onBlur, value } }) => (
                                            <AsyncAutocomplete
                                                onChange={onArticleChange}
                                                onBlur={onBlur}
                                                value={value}
                                                withPos={true}
                                            />
                                        )}
                                        control={control}
                                        name="article"
                                        defaultValue={null}
                                        rules={{
                                            required: { value: true, message: t("Feld darf nicht leer sein") },
                                        }}
                                    />
                                    {/* @ts-ignore */}
                                    {errors.article && <div style={{ textAlign: "left", color: "#eb445a", fontSize: 14 }}>{errors.article.message}</div>}
                                </div>
                                <IonButton fill="clear" onClick={() => startScan()} disabled={!isScannerSupported}>
                                    <IonIcon slot="icon-only" icon={scanOutline} />
                                </IonButton>
                            </div>

                            {/* Durchmesser */}
                            <div style={{ marginTop: 10 }}>
                                <Controller
                                    render={({ field: { onBlur, value } }) => (
                                        <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                            <InputLabel id="durchmesser">{t("calculatorForm.MillerForm.Durchmesser")}</InputLabel>
                                            <Select
                                                onChange={onDiameterChange}
                                                onBlur={onBlur}
                                                value={value?.toString()}
                                                variant="standard"
                                                labelId="durchmesser"
                                                disabled={selectedArticle ? false : true}
                                            >
                                                {diameters.map((diameter) =>
                                                    <MenuItem
                                                        key={diameter}
                                                        value={diameter}
                                                    >{diameter} mm</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                    control={control}
                                    name="diameter"
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                    }}
                                />
                                {/* @ts-ignore */}
                                {errors.diameter && <div style={{ textAlign: "left", color: "#eb445a", fontSize: 14 }}>{errors.diameter.message}</div>}
                            </div>

                            {/* Position */}
                            <div style={{ marginTop: 10 }}>
                                <Controller
                                    render={({ field: { onBlur, value } }) => (
                                        <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                            <InputLabel id="pos">{t("calculatorForm.MillerForm.Variante")}</InputLabel>
                                            <Select
                                                onChange={onPosChange}
                                                onBlur={onBlur}
                                                value={value}
                                                variant="standard"
                                                labelId="pos"
                                                disabled={selectedDiameter === "" ? true : false}
                                            >
                                                {variantOptions.map(variantOption => {
                                                    if (variantOption.d1 && variantOption.d1.toString() === selectedDiameter) {
                                                        return (
                                                            <MenuItem
                                                                key={variantOption.id}
                                                                value={variantOption.pos}
                                                            >
                                                                {variantOption.ident + "-" + variantOption.pos + " (d1:" + variantOption.d1 + ", l2:" + variantOption.l2 + ", l1:" + variantOption.l1 + ")"}
                                                            </MenuItem>

                                                        )
                                                    }
                                                    return null
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}
                                    control={control}
                                    name="pos"
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                    }}
                                />
                                {/* @ts-ignore */}
                                {errors.pos && <div style={{ textAlign: "left", color: "#eb445a", fontSize: 14 }}>{errors.pos.message}</div>}
                            </div>

                            {/* Material */}
                            <div style={{ marginTop: 24 }} className="stepTitle">{t("calculatorForm.MillerForm.Material")}</div>

                            <div style={{ width: "100%" }}>
                                <Controller
                                    render={({ field: { onChange, onBlur, value } }) => (
                                        <AsyncMaterialsAutocomplete
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            value={value}
                                            ident={selectedIdent}
                                            pos={selectedPos}
                                            disabled={selectedPos === '' ? true : false}
                                        />
                                    )}
                                    control={control}
                                    name="material"
                                    defaultValue={null}
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                    }}
                                />
                                {/* @ts-ignore */}
                                {errors.material && <div style={{ textAlign: "left", color: "#eb445a", fontSize: 14 }}>{errors.material.message}</div>}
                            </div>

                            <IonButton style={{ marginTop: 24 }} onClick={handleSubmit(onSubmit)} expand="block"><IonIcon slot="start" icon={arrowDownOutline} />{t("calculatorForm.MillerForm.Bearbeitung")}</IonButton>
                        </form>
                    </IonCardContent>
                </IonCard >
                <IonModal ref={modal} isOpen={scanActive} className="qrcodeScannerModal" onWillDismiss={() => stopScan()} canDismiss={true}>
                    <MLKitScanner modal={modal} />
                </IonModal>
            </>
        )
    } else {
        if (data) {
            return (
                <IonCard color="light" className="millerFormCompletedCard" style={{ marginTop: 20 }}>
                    <IonCardContent>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div>
                                <div><b>{t("calculatorForm.MillerForm.Fräser")}:</b> {data.ident}-{data.pos}</div>
                                <div><b>{t("calculatorForm.MillerForm.Material")}:</b> {getMaterialLabel(data.material!)}</div>
                            </div>
                            <div>
                                <IonButton fill="outline" onClick={editBtnCallback}>
                                    <IonIcon icon={createOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    </IonCardContent>
                </IonCard>
            )
        }
        return null
    }

}

export default MillerForm;